/**
 * Chooses between popup and full-page redirect for web OAuth sign-in.
 *
 * Popup is the reliable default on desktop browsers. Firebase's redirect flow
 * routes through the handler on `<project>.firebaseapp.com`, and modern
 * browsers (Chrome third-party-cookie phase-out, Safari ITP, Firefox ETP)
 * block the cross-origin storage it depends on — so after returning to
 * turaq.kg, getRedirectResult() is empty and the sign-in silently fails.
 *
 * Redirect is only needed where popups themselves are blocked: iOS Safari and
 * embedded in-app WebViews (Instagram, Facebook, etc.), where a popup opened
 * after an `await` in the click handler is refused.
 */

const isIOSWeb = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as "Macintosh" — tell it apart by touch support.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

export const isEmbeddedBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|MicroMessenger|GSA\/|musical_ly|Bytedance|TikTok|Snapchat|Pinterest|LinkedInApp|; wv\)|\bWebView\b/i.test(
    ua,
  );
};

export const isAndroid = (): boolean =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '');

/**
 * Google refuses OAuth inside in-app browsers ("403 disallowed_useragent"),
 * so there the only way to Google sign-in is the phone's real browser.
 * Opens the current page there: Chrome via an intent on Android, Safari via
 * the x-safari-https scheme on iOS 17+. Returns false if nothing could be
 * tried (the caller then shows the manual "⋯ → Open in browser" hint).
 */
export const openInSystemBrowser = (url: string = window.location.href): boolean => {
  try {
    const u = new URL(url);
    if (isAndroid()) {
      window.location.href = `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(u.href)};end`;
      return true;
    }
    if (isIOSWeb()) {
      window.location.href = `x-safari-${u.href}`;
      return true;
    }
  } catch {
    /* fall through to the manual hint */
  }
  return false;
};

export const shouldUseOAuthRedirect = (): boolean =>
  isIOSWeb() || isEmbeddedBrowser();
